import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton, Tooltip,
  Link, Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AdUnitsIcon from '@mui/icons-material/AdUnits';
import ImageIcon from '@mui/icons-material/Image';
import VideocamIcon from '@mui/icons-material/Videocam';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import LinkIcon from '@mui/icons-material/Link';
import TimerIcon from '@mui/icons-material/Timer';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { getAds, createAd, updateAd, deleteAd, getAdPlacements, getAdModels } from '../../api/advertiserApi';
import { submitApproval } from '../../api/approvalApi';
import LocalText from '../common/LocalText';
import { useLocalization } from '../../context/LocalizationContext';
import { useCurrency } from '../../context/CurrencyContext';
import { useToast } from '../../context/ToastContext';
import { CAMPAIGN_STATUSES, deriveStatus } from './advertiserConstants';
import CreateAdModal from './CreateAdModal';
import { modelPreviewUrl } from './AdModelPicker';
import AdPassedDatesDialog from './AdPassedDatesDialog';
import ConfirmModal from '../form-components/ConfirmModal';
import ApprovalStatusBadge from '../approval/ApprovalStatusBadge';
import SubmitForApprovalButton from '../approval/SubmitForApprovalButton';
import WithdrawButton from '../approval/WithdrawButton';
import ContentPreviewDrawer from '../approval/ContentPreviewDrawer';
import StatusTabs from '../approval/StatusTabs';
import { buildVersionedList, filterVersionedList, countVersionedTabs } from '../../utils/versionedContent';
import { CONTENT_TYPES } from '../../constants/contentTypes';
import { signalRService } from '../../services/signalRService';
import { SIGNALR_EVENTS } from '../../constants/signalREvents';
import { usePermissions } from '../../hooks/usePermissions';
import { adHasStarted, formatDateRanges, splitRunDates, todayEastern } from '../../utils/dateUtils';

const deriveMediaType = (ad) => {
  if (ad.is3d || ad.mediaTypeId === 4) return '3D';
  if (ad.mediaTypeId === 2) return 'Video';
  return 'Image';
};

const mediaIcon = (type) => {
  if (type === 'Video') return <VideocamIcon sx={{ fontSize: 16 }} />;
  if (type === '3D')    return <ViewInArIcon  sx={{ fontSize: 16 }} />;
  return <ImageIcon sx={{ fontSize: 16 }} />;
};

const campaignStatusStyle = (status) => {
  const match = CAMPAIGN_STATUSES.find((s) => s.id === status);
  const color = match?.color ?? '#757575';
  return { chipBg: `${color}18`, chipColor: color, chipBorder: `${color}40` };
};

const fmt = (dateStr) =>
  dateStr ? new Date(`${dateStr}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const truncateUrl = (url) => {
  if (!url) return null;
  try {
    const u = new URL(url);
    const path = u.pathname;
    return path.length > 28 ? `…${path.slice(-26)}` : path;
  } catch {
    return url.length > 30 ? `${url.slice(0, 28)}…` : url;
  }
};

// The Media URL column of a 3D ad: the preview image link of its model, shown like a 2D media link (the model name on hover).
// A model without a preview (or one the registry list did not return) shows its name instead.
const ModelMediaCell = ({ ad, models }) => {
  const ref = ad.model;
  if (!ref) return <Typography variant="body2" sx={{ color: '#c8d8ea', fontSize: 12 }}>—</Typography>;
  const url = modelPreviewUrl(models.find((m) => m.id === ref.id));
  if (url) {
    return (
      <Tooltip title={`${ref.displayName}: ${url}`} placement="top">
        <Link
          href={url}
          target="_blank"
          rel="noreferrer"
          underline="hover"
          data-testid={`ad-model-cell-${ad.id}`}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#5383b3', fontSize: 12 }}
          onClick={(e) => e.stopPropagation()}
        >
          <LinkIcon sx={{ fontSize: 13 }} />
          {truncateUrl(url)}
        </Link>
      </Tooltip>
    );
  }
  return (
    <Box data-testid={`ad-model-cell-${ad.id}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#5383b3', fontSize: 12 }}>
      <ViewInArIcon sx={{ fontSize: 13 }} />
      {ref.displayName}
    </Box>
  );
};

const COLUMNS = ['Ad Name', 'Placement', 'Media Type', 'Media URL', 'Duration', 'Run Dates', 'Approval Status', 'Actions'];

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

const infoChipSx = (bg, color, border) => ({ bgcolor: bg, color, fontWeight: 600, fontSize: 10, height: 20, border: `1px solid ${border}` });

const viewBtnSx = {
  borderColor: '#3b82f6', color: '#1d4ed8', bgcolor: '#eff6ff',
  borderRadius: '8px', px: 1.5, py: 0.5, fontSize: 11, fontWeight: 700,
  textTransform: 'uppercase', letterSpacing: 0.4, whiteSpace: 'nowrap',
  boxShadow: 'none',
  '&:hover': { bgcolor: '#dbeafe', borderColor: '#2563eb', boxShadow: 'none' },
};

const AdvertiserAdTable = ({ campaign, advertiserApprovalStatus = null, overrideSpaceId = null }) => {
  const { locale } = useLocalization();
  const { toast }  = useToast();
  const { canFeature } = usePermissions();
  const canEdit = canFeature('/advertisers', 'advertiser-edit');
  const { activeCurrency } = useCurrency();
  const currencySymbol = activeCurrency?.symbol ?? '$';

  const isAdvertiserLocked = advertiserApprovalStatus === 'pending' || advertiserApprovalStatus === 'rejected';
  const isCampaignLocked   = campaign.approvalStatus === 'pending' || campaign.approvalStatus === 'rejected';
  const isParentLocked     = isAdvertiserLocked || isCampaignLocked;

  const parentLockReason = isAdvertiserLocked
    ? `The advertiser has a ${advertiserApprovalStatus} approval`
    : isCampaignLocked
      ? `This campaign has a ${campaign.approvalStatus} approval`
      : '';
  const campaignName =
    campaign.localizedName?.values?.find((v) => v.localeId === locale)?.value ||
    campaign.localizedName?.values?.[0]?.value ||
    '';

  const [ads,               setAds]               = useState([]);
  const [placements,        setPlacements]        = useState([]);
  // The 3D model registry of the space (read only here). Loaded next to the placements and again whenever a modal opens,
  // because models are added from Postman while the CMS is open.
  const [models,            setModels]            = useState([]);
  const [modelsLoading,     setModelsLoading]     = useState(false);
  const [modelsFailed,      setModelsFailed]      = useState(false);   // the last load failed (not the same as "no models")
  const [tabValue,          setTabValue]          = useState('live');
  const [highlight,         setHighlight]         = useState({ tab: null, token: 0 });
  const flashTab = useCallback((tab) => setHighlight({ tab, token: Date.now() }), []);
  const [modalOpen,         setModalOpen]         = useState(false);
  const [editTarget,        setEditTarget]        = useState(null);
  const [approvalMode,      setApprovalMode]      = useState(false);
  const [hoveredRow,        setHoveredRow]        = useState(null);
  const [viewTarget,        setViewTarget]        = useState(null);
  // "Duplicate as new ad": the ad whose details a new ad starts from.
  const [duplicateSource,   setDuplicateSource]   = useState(null);
  // A draft whose run dates have passed cannot be submitted as it is: the user fixes the dates in a dialog first.
  const [passedTarget,      setPassedTarget]      = useState(null);
  const [deleteTarget,      setDeleteTarget]      = useState(null);
  const [proposeDeleteTarget, setProposeDeleteTarget] = useState(null);
  const [viewProposalOpen,  setViewProposalOpen]  = useState(false);
  const [viewApprovalRecord, setViewApprovalRecord] = useState(null);
  // Only depends on campaign identity — startDate/endDate are not used by getAds.
  useEffect(() => {
    let cancelled = false;
    getAds(campaign.advertiserId, campaign.id, overrideSpaceId)
      .then((data) => {
        if (cancelled) return;
        setAds(data);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [campaign.advertiserId, campaign.id, overrideSpaceId]);

  useEffect(() => {
    let cancelled = false;
    getAdPlacements(overrideSpaceId)
      .then((data) => { if (!cancelled) setPlacements(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [overrideSpaceId]);

  // Monotonic counter: if two loads race (opening the modal right after the page loaded), only the latest one applies.
  const modelsLoadIdRef = useRef(0);
  const loadModels = useCallback(() => {
    const id = ++modelsLoadIdRef.current;
    setModelsLoading(true);
    getAdModels(overrideSpaceId)
      .then((data) => { if (id === modelsLoadIdRef.current) { setModels(Array.isArray(data) ? data : []); setModelsFailed(false); } })
      .catch(() => { if (id === modelsLoadIdRef.current) setModelsFailed(true); })
      .finally(() => { if (id === modelsLoadIdRef.current) setModelsLoading(false); });
  }, [overrideSpaceId]);

  useEffect(() => { loadModels(); }, [loadModels]);
  useEffect(() => { if (modalOpen || viewTarget) loadModels(); }, [modalOpen, viewTarget, loadModels]);

  // Monotonic counter: if two load() calls race, only the latest one applies.
  const loadIdRef = useRef(0);
  const load = useCallback(async () => {
    const id = ++loadIdRef.current;
    try {
      const data = await getAds(campaign.advertiserId, campaign.id, overrideSpaceId);
      if (id !== loadIdRef.current) return; // a newer load() already resolved
      setAds(data);
    } catch {}
  }, [campaign.advertiserId, campaign.id, overrideSpaceId]);

  useEffect(() => {
    const handleDecision = (payload) => {
      if (payload?.contentType === CONTENT_TYPES.AD) load();
    };
    signalRService.on(SIGNALR_EVENTS.APPROVAL_DECISION, handleDecision);
    return () => signalRService.off(SIGNALR_EVENTS.APPROVAL_DECISION, handleDecision);
  }, [load]);

  useEffect(() => {
    const handleWithdrawn = (payload) => {
      if (payload?.contentType === CONTENT_TYPES.AD) load();
    };
    signalRService.on(SIGNALR_EVENTS.APPROVAL_WITHDRAWN, handleWithdrawn);
    return () => signalRService.off(SIGNALR_EVENTS.APPROVAL_WITHDRAWN, handleWithdrawn);
  }, [load]);

  const adName = (ad) =>
    ad?.localizedName?.values?.find((v) => v.localeId === locale)?.value ||
    ad?.localizedName?.values?.[0]?.value || '';

  // Re-throws so CreateAdModal's handleSubmit can keep the modal open on failure.
  // The global api-error interceptor in apiConfig.js already shows the error toast.
  const handleSave = async (data) => {
    try {
      let needsApproval = false;
      if (editTarget) {
        // Golden rule: direct edit only when contentStatus='draft' AND no ca record (approvalId null).
        const canDirect =
          editTarget._viewType !== 'snapshot' &&
          editTarget.contentStatus === 'draft' &&
          !editTarget.approvalId;
        needsApproval = !canDirect;

        if (needsApproval) {
          const liveId = editTarget._liveId ?? editTarget.id;
          await updateAd(campaign.advertiserId, campaign.id, liveId, data);
          toast.success('Edit proposal submitted for approval.');
        } else {
          await updateAd(campaign.advertiserId, campaign.id, editTarget.id, data);
          toast.success('Ad updated successfully.');
        }
      } else {
        await createAd(campaign.advertiserId, campaign.id, data);
        toast.success('Ad created and moved to Draft.');
      }
      setEditTarget(null);
      setDuplicateSource(null);
      setApprovalMode(false);
      await load();
      flashTab(needsApproval ? 'pending' : 'draft');
    } catch (err) {
      throw err;
    }
  };

  const hasPassedDates = (ad) => (ad.scheduleDates ?? []).some((d) => String(d).slice(0, 10) < todayEastern());

  const handleDuplicate = (ad) => {
    setEditTarget(null);
    setDuplicateSource(ad);
    setApprovalMode(false);
    setModalOpen(true);
  };

  const handleEdit = (ad) => {
    const canDirect = ad._viewType !== 'snapshot' && ad.contentStatus === 'draft' && !ad.approvalId;
    setEditTarget(ad);
    setApprovalMode(!canDirect);
    setModalOpen(true);
  };

  const handleDelete = (ad) => {
    const canDirect = ad.contentStatus === 'draft' && !ad.approvalId;
    if (canDirect) {
      setDeleteTarget(ad);
    } else {
      setProposeDeleteTarget(ad);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteAd(campaign.advertiserId, campaign.id, deleteTarget.id);
      setDeleteTarget(null);
      await load();
    } catch {
      setDeleteTarget(null);
    }
  };

  const confirmProposeDelete = async () => {
    if (!proposeDeleteTarget) return;
    try {
      const contentName = adName(proposeDeleteTarget) || `Ad ${proposeDeleteTarget.id}`;
      await submitApproval({
        contentType: CONTENT_TYPES.AD,
        contentId: proposeDeleteTarget.id,
        contentName: contentName,
        actionType: 'delete',
      });
      toast.success('Deletion request submitted for approval.');
      setProposeDeleteTarget(null);
      await load();
      flashTab('pending');
    } catch {
      setProposeDeleteTarget(null);
    }
  };

  const openViewDrawer = (ad) => {
    setViewApprovalRecord({
      approvalId:      ad.approvalId,
      actionType:      ad.approvalActionType,
      contentType:     CONTENT_TYPES.AD,
      sectionLabel:    'Ad',
      contentName:     adName(ad),
      status:          ad.approvalStatus,
      _liveId:         ad._liveId ?? ad.id,
    });
    setViewProposalOpen(true);
  };

  const campaignHasEnded = Boolean(campaign.endDate) && String(campaign.endDate).slice(0, 10) < todayEastern();
  const campaignStatus  = campaign.status ?? deriveStatus(campaign.startDate, campaign.endDate);
  const { chipBg, chipColor, chipBorder } = campaignStatusStyle(campaignStatus);

  return (
    <Box>
      {isParentLocked && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: '10px' }}>
          {parentLockReason}. Ads cannot be added or modified until the approval is resolved.
        </Alert>
      )}

      {/* Campaign banner */}
      <Box
        sx={{
          bgcolor: '#0a326d', borderRadius: '16px', p: 2.5, mb: 3,
          display: 'flex', flexDirection: 'column', gap: 1.5,
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, mb: 0.3, textTransform: 'uppercase', letterSpacing: 0.8 }}>
              <LocalText>Campaign</LocalText>
            </Typography>
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, lineHeight: 1.2 }}>
              {campaignName}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', mt: 0.5, display: 'block' }}>
              {fmt(campaign.startDate)} — {fmt(campaign.endDate)}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={2}>
            {campaign.costPerDay > 0 && (
              <Box textAlign="right">
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 10, display: 'block' }}>
                  <LocalText>Cost / Day</LocalText>
                </Typography>
                <Typography variant="body2" sx={{ color: 'white', fontWeight: 700, fontSize: 15 }}>
                  {currencySymbol}{campaign.costPerDay?.toLocaleString()}
                </Typography>
              </Box>
            )}
            <Chip
              label={campaignStatus}
              size="small"
              sx={{ height: 24, fontSize: 11, fontWeight: 700, bgcolor: chipBg, color: chipColor, border: `1px solid ${chipBorder}` }}
            />
            {canEdit && (
              <Tooltip title={isParentLocked ? `${parentLockReason} — resolve it first` : ''}>
                <span>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    disabled={isParentLocked}
                    onClick={(e) => { e.currentTarget.blur(); setModalOpen(true); }}
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.15)', color: 'white', border: '1.5px solid rgba(255,255,255,0.3)',
                      borderRadius: '10px', px: 2, py: 0.8, fontWeight: 600, fontSize: 13, textTransform: 'none',
                      boxShadow: 'none', backdropFilter: 'blur(4px)',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.25)', borderColor: 'rgba(255,255,255,0.5)' },
                      '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.35)', borderColor: 'rgba(255,255,255,0.15)' },
                    }}
                  >
                    <LocalText>Add Ad</LocalText>
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>
        </Box>

      </Box>

      {/* Approval status filter tabs */}
      <StatusTabs items={ads} counts={countVersionedTabs(ads, 'id')} value={tabValue} onChange={setTabValue} sx={{ mb: 1 }} highlightTab={highlight.tab} highlightToken={highlight.token} />

      {/* Table */}
      {ads.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'white', borderRadius: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <AdUnitsIcon sx={{ fontSize: 52, color: '#c8d8ea', mb: 1.5 }} />
          <Typography variant="body1" sx={{ color: '#85a5c6', fontWeight: 500 }}>
            <LocalText>No ads yet. Add one!</LocalText>
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#EAF0F6' }}>
                {COLUMNS.map((h) => (
                  <TableCell
                    key={h}
                    sx={{ fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.8, color: '#5383b3', py: 1.5, borderBottom: '1px solid #dde6f0' }}
                  >
                    <LocalText>{h}</LocalText>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filterVersionedList(buildVersionedList(ads, 'id'), tabValue).map((ad) => {
                const mediaType      = deriveMediaType(ad);
                const firstUrl      = ad.mediaLinks?.[0]?.mediaLink ?? '';
                const hasDuration   = ad.durationSeconds > 0;
                const isSnapshot    = ad._viewType === 'snapshot';
                const rowKey        = isSnapshot ? `snapshot-${ad._liveId}` : `live-${ad.id}`;
                const isLocked      = !isSnapshot && (ad.approvalStatus === 'pending' || ad.approvalStatus === 'rejected');
                const canDirectEdit = !isSnapshot && ad.contentStatus === 'draft' && !isLocked;

                return (
                  <TableRow
                    key={rowKey}
                    onMouseEnter={() => setHoveredRow(rowKey)}
                    onMouseLeave={() => setHoveredRow(null)}
                    sx={{
                      bgcolor: isSnapshot
                        ? (ad.approvalStatus === 'pending' ? '#fffdf0' : '#fff8f8')
                        : hoveredRow === rowKey ? '#f7faff' : 'white',
                      borderLeft: isSnapshot
                        ? `3px solid ${ad.approvalStatus === 'pending' ? '#f59e0b' : '#ef4444'}`
                        : 'none',
                      transition: 'background-color 0.15s ease',
                      '& td': { borderBottom: '1px solid #EAF0F6', py: 1.5 },
                    }}
                  >
                    {/* Ad Name */}
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e3a5f' }}>
                        {ad.localizedName?.values?.find((v) => v.localeId === locale)?.value || ad.localizedName?.values?.[0]?.value || ''}
                      </Typography>
                    </TableCell>

                    {/* Placement — live rows have placementKey; snapshot rows only have placementId */}
                    <TableCell>
                      {(() => {
                        // The live list wins: a proposal stored before the server refreshed the key can carry the old one.
                        const key = placements.find((p) => p.id === ad.placementId)?.placementKey ?? ad.placementKey;
                        return key ? (
                          <Box display="flex" flexDirection="column" alignItems="flex-start" gap={0.5}>
                            <Chip
                              label={key}
                              size="small"
                              sx={{ bgcolor: '#EAF0F6', color: '#0a326d', fontWeight: 600, fontSize: 11, height: 22, border: '1px solid #c8d8ea' }}
                            />
                            {/* A placement can only be deleted once no active/upcoming ad uses it. If this ad ran there it is
                                history; if it never did, it simply will not run until it gets a live placement. */}
                            {ad.placementDeleted && !placements.some((p) => p.id === ad.placementId) && (
                              <Tooltip
                                title={<LocalText>{adHasStarted(ad)
                                  ? 'This ad ran on a placement that has since been removed. It is kept as history.'
                                  : "This ad's placement has been removed, so it will not run. Edit the ad to choose another placement."}</LocalText>}
                                placement="top"
                              >
                                <Chip
                                  label={<LocalText>Placement removed</LocalText>}
                                  size="small"
                                  data-testid="placement-removed-badge"
                                  sx={{ bgcolor: '#fff4e5', color: '#b45309', fontWeight: 600, fontSize: 10, height: 20, border: '1px solid #fcd9a8' }}
                                />
                              </Tooltip>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" sx={{ color: '#c8d8ea', fontSize: 12 }}>—</Typography>
                        );
                      })()}
                    </TableCell>

                    {/* Media Type */}
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.8} sx={{ color: '#5383b3' }}>
                        {mediaIcon(mediaType)}
                        <Typography variant="body2" sx={{ color: '#5383b3', fontSize: 13 }}>
                          {mediaType}
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* Media URL (a 3D ad shows the preview image link of its model) */}
                    <TableCell>
                      {mediaType === '3D' ? (
                        <ModelMediaCell ad={ad} models={models} />
                      ) : firstUrl ? (
                        <Tooltip title={firstUrl} placement="top">
                          <Link
                            href={firstUrl}
                            target="_blank"
                            rel="noreferrer"
                            underline="hover"
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#5383b3', fontSize: 12 }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <LinkIcon sx={{ fontSize: 13 }} />
                            {truncateUrl(firstUrl)}
                          </Link>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" sx={{ color: '#c8d8ea', fontSize: 12 }}>—</Typography>
                      )}
                    </TableCell>

                    {/* Duration */}
                    <TableCell>
                      {hasDuration ? (
                        <Box display="flex" alignItems="center" gap={0.5} sx={{ color: '#5383b3' }}>
                          <TimerIcon sx={{ fontSize: 14 }} />
                          <Typography variant="body2" sx={{ fontSize: 13 }}>{ad.durationSeconds}s</Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" sx={{ color: '#c8d8ea', fontSize: 12 }}>—</Typography>
                      )}
                    </TableCell>

                    {/* Run Dates — inside the campaign's current dates, plus a note for anything outside them */}
                    <TableCell>
                      {(() => {
                        const split    = splitRunDates(ad.scheduleDates, campaign.startDate, campaign.endDate);
                        const ran      = ad.contentStatus === undefined || ad.contentStatus === 'approved';   // only an approved ad has ever run
                        const todayET  = todayEastern();
                        const current  = ran ? split.current : split.current.filter((d) => d >= todayET);
                        const history  = ran ? split.history : [];
                        const passed   = ran ? [] : [...split.history, ...split.current.filter((d) => d < todayET)].sort();
                        const stale    = split.stale;
                        if (current.length + history.length + passed.length + stale.length === 0) {
                          return <Typography variant="body2" sx={{ color: '#c8d8ea', fontSize: 12 }}>—</Typography>;
                        }
                        return (
                          <Box display="flex" flexDirection="column" alignItems="flex-start" gap={0.5}>
                            {current.length > 0 ? (
                              <Box data-testid="run-dates-current">
                                <Typography variant="body2" sx={{ fontSize: 12, fontWeight: 600, color: '#1e3a5f' }}>
                                  {formatDateRanges(current, 2)}
                                </Typography>
                                <Typography sx={{ fontSize: 11, color: '#64748b' }}>{plural(current.length, 'day')}</Typography>
                              </Box>
                            ) : (
                              <Typography data-testid="run-dates-none" sx={{ fontSize: 12, color: '#b45309' }}>
                                <LocalText>No run dates in this campaign</LocalText>
                              </Typography>
                            )}
                            {/* Scheduled before today under an earlier campaign window: kept on record, read-only */}
                            {history.length > 0 && (
                              <Tooltip title={`Earlier run dates (read-only): ${formatDateRanges(history, 6)}`} placement="top">
                                <Chip
                                  label={`${plural(history.length, 'earlier day')}`}
                                  size="small"
                                  data-testid="earlier-run-dates-chip"
                                  sx={infoChipSx('#f1f5f9', '#475569', '#dde6f0')}
                                />
                              </Tooltip>
                            )}
                            {/* A draft's days that passed: the ad never ran on them, so they must be replaced before it can be submitted */}
                            {passed.length > 0 && (
                              <Tooltip title={`Already passed, and this ad never ran on them. Update the dates before submitting: ${formatDateRanges(passed, 6)}`} placement="top">
                                <Chip
                                  label={`${plural(passed.length, 'passed day')}`}
                                  size="small"
                                  data-testid="passed-run-dates-chip"
                                  sx={infoChipSx('#fff4e5', '#b45309', '#fcd9a8')}
                                />
                              </Tooltip>
                            )}
                            {/* Today or later but outside the campaign's dates: can't run, removed on the next save */}
                            {stale.length > 0 && (
                              <Tooltip title={`Outside this campaign's dates, will not run: ${formatDateRanges(stale, 6)}`} placement="top">
                                <Chip
                                  label={`${plural(stale.length, 'day')} outside campaign`}
                                  size="small"
                                  data-testid="stale-run-dates-chip"
                                  sx={infoChipSx('#fff4e5', '#b45309', '#fcd9a8')}
                                />
                              </Tooltip>
                            )}
                          </Box>
                        );
                      })()}
                    </TableCell>

                    {/* Approval Status */}
                    <TableCell>
                      {ad.approvalId ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {/* Live cards show contentStatus badge; snapshot cards show approvalStatus */}
                          <ApprovalStatusBadge
                            status={!isSnapshot ? (ad.contentStatus ?? ad.approvalStatus) : ad.approvalStatus}
                            actionType={ad.approvalActionType}
                          />

                          {/* Snapshot: comparison view (before vs after) */}
                          {isSnapshot && ad.approvalId && (
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<VisibilityIcon sx={{ fontSize: 13 }} />}
                              onClick={() => openViewDrawer(ad)}
                              sx={viewBtnSx}
                            >
                              View
                            </Button>
                          )}

                          {/* Pending snapshot: Withdraw */}
                          {isSnapshot && ad.approvalStatus === 'pending' && ad.approvalId && canEdit && (
                            <WithdrawButton approvalId={ad.approvalId} onWithdrawn={() => { load(); flashTab(ad.approvalActionType === 'create' ? 'draft' : 'live'); }} />
                          )}

                          {/* Rejected snapshot: Dismiss only — Edit & Resubmit lives inside the View drawer */}
                          {isSnapshot && ad.approvalStatus === 'rejected' && ad.approvalId && canEdit && (
                            <WithdrawButton approvalId={ad.approvalId} onWithdrawn={() => { load(); flashTab(ad.approvalActionType === 'create' ? 'draft' : 'live'); }} variant="dismiss" />
                          )}
                        </Box>
                      ) : (
                        /* Draft ad with no ca record: offer first submit for approval */
                        canDirectEdit && ad.contentStatus === 'draft' && canEdit ? (
                          <SubmitForApprovalButton
                            contentType={CONTENT_TYPES.AD}
                            contentId={ad.id}
                            contentName={adName(ad)}
                            onBeforeSubmit={() => {
                              if (!hasPassedDates(ad)) return false;
                              setPassedTarget(ad);
                              return true;
                            }}
                            onSubmitted={() => { load(); flashTab('pending'); }}
                          />
                        ) : (
                          <Typography variant="body2" sx={{ color: '#c8d8ea', fontSize: 12 }}>—</Typography>
                        )
                      )}
                    </TableCell>

                    {/* Actions */}
                    <TableCell>
                      <Box display="flex" gap={0.5}>
                        {/* Eye — view readonly */}
                        <Tooltip title="View (read-only)">
                          <IconButton
                            size="small"
                            onClick={(e) => { e.currentTarget.blur(); setViewTarget(ad); }}
                            sx={{ '&:hover': { bgcolor: '#EAF0F6' } }}
                          >
                            <VisibilityIcon sx={{ fontSize: 15, color: '#5383b3' }} />
                          </IconButton>
                        </Tooltip>
                        {canEdit && (
                          <Tooltip title={
                            isSnapshot ? 'Duplicate the live ad instead'
                              : isParentLocked ? `${parentLockReason} — resolve it first`
                              : campaignHasEnded ? 'This campaign has ended — a new ad needs a campaign that is still running'
                              : 'Duplicate as new ad'
                          }>
                            <span>
                              <IconButton
                                size="small"
                                aria-label={`Duplicate ${adName(ad) || 'ad'} as new ad`}
                                disabled={isSnapshot || isParentLocked || campaignHasEnded}
                                onClick={(e) => { e.currentTarget.blur(); handleDuplicate(ad); }}
                                sx={{ '&:hover': { bgcolor: '#EAF0F6' } }}
                              >
                                <ContentCopyIcon sx={{ fontSize: 15, color: (isSnapshot || isParentLocked || campaignHasEnded) ? '#ccc' : '#0a326d' }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {canEdit && (
                          <Tooltip title={
                            isSnapshot
                              ? 'View or Dismiss via the Approval Status column'
                              : isParentLocked
                                ? `${parentLockReason} — resolve it first`
                                : isLocked
                                  ? ad.approvalStatus === 'pending' ? 'Withdraw proposal first' : 'Dismiss proposal first'
                                  : canDirectEdit ? 'Edit' : 'Propose edit (requires approval)'
                          }>
                            <span>
                              <IconButton
                                size="small"
                                disabled={isSnapshot || isLocked || isParentLocked}
                                onClick={(e) => { e.currentTarget.blur(); handleEdit(ad); }}
                                sx={{ '&:hover': { bgcolor: '#EAF0F6' } }}
                              >
                                <EditIcon sx={{ fontSize: 15, color: (isSnapshot || isLocked || isParentLocked) ? '#ccc' : '#0a326d' }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {canEdit && (
                          <Tooltip title={
                            isSnapshot
                              ? 'Use Dismiss in the Approval Status column'
                              : isParentLocked
                                ? `${parentLockReason} — resolve it first`
                                : isLocked
                                  ? ad.approvalStatus === 'pending' ? 'Withdraw proposal first' : 'Dismiss proposal first'
                                  : canDirectEdit ? 'Delete' : 'Propose deletion (requires approval)'
                          }>
                            <span>
                              <IconButton
                                size="small"
                                disabled={isSnapshot || isLocked || isParentLocked}
                                onClick={(e) => { e.currentTarget.blur(); handleDelete(ad); }}
                                sx={{ '&:hover': { bgcolor: '#fff0f0' } }}
                              >
                                <DeleteIcon sx={{ fontSize: 15, color: (isSnapshot || isLocked || isParentLocked) ? '#ccc' : '#d32f2f' }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Read-only view drawer */}
      <CreateAdModal
        open={Boolean(viewTarget)}
        onClose={() => setViewTarget(null)}
        onSave={() => {}}
        initial={viewTarget}
        campaign={campaign}
        placements={placements}
        models={models}
        modelsLoading={modelsLoading}
        modelsLoadFailed={modelsFailed}
        readOnly
        overrideSpaceId={overrideSpaceId}
      />

      <CreateAdModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditTarget(null); setDuplicateSource(null); setApprovalMode(false); }}
        onSave={handleSave}
        initial={editTarget}
        approvalMode={approvalMode}
        campaign={campaign}
        placements={placements}
        models={models}
        modelsLoading={modelsLoading}
        modelsLoadFailed={modelsFailed}
        overrideSpaceId={overrideSpaceId}
        liveAd={editTarget ? (ads.find((a) => a.id === (editTarget._liveId ?? editTarget.id)) ?? null) : null}
        template={duplicateSource}
      />

      {/* A draft with passed run dates: fix them here, then it is submitted in the same step */}
      <AdPassedDatesDialog
        open={Boolean(passedTarget)}
        ad={passedTarget}
        adName={passedTarget ? adName(passedTarget) : ''}
        campaign={campaign}
        placements={placements}
        overrideSpaceId={overrideSpaceId}
        onClose={() => setPassedTarget(null)}
        onDone={() => { setPassedTarget(null); load(); flashTab('pending'); }}
      />

      {/* Direct delete confirm (draft ads) */}
      <ConfirmModal
        open={Boolean(deleteTarget)}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        severity="danger"
        title="Delete Ad"
        message={`Delete "${adName(deleteTarget) || 'this ad'}"?`}
        confirmLabel="Delete"
      />

      {/* Propose-delete confirm (live/approved ads) */}
      <ConfirmModal
        open={Boolean(proposeDeleteTarget)}
        onCancel={() => setProposeDeleteTarget(null)}
        onConfirm={confirmProposeDelete}
        severity="warning"
        title="Propose Ad Deletion"
        message={`Submit a deletion request for "${adName(proposeDeleteTarget) || 'this ad'}"? The ad will remain active until an approver approves.`}
        confirmLabel="Submit for Approval"
      />

      {/* View proposal drawer */}
      {viewApprovalRecord && (
        <ContentPreviewDrawer
          open={viewProposalOpen}
          approvalRecord={viewApprovalRecord}
          onClose={() => { setViewProposalOpen(false); setViewApprovalRecord(null); }}
          readOnly
          onEdit={canEdit ? (proposedSnap) => {
            setViewProposalOpen(false);
            const liveId = viewApprovalRecord._liveId;
            setEditTarget({ ...(proposedSnap || {}), id: liveId, _viewType: 'snapshot', _liveId: liveId });
            setApprovalMode(true);
            setViewApprovalRecord(null);
            setModalOpen(true);
          } : undefined}
        />
      )}
    </Box>
  );
};

export default AdvertiserAdTable;
